import type { FBMessage } from '@/types/fb-type';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

const BOTTOM_THRESHOLD = 80;
const TOP_THRESHOLD = 80;

type UseChatWindowScrollParams = {
  conversationId?: string;
  messages: FBMessage[];
  isLoadingMessage: boolean;
  isLoadingMore: boolean;
  prevCursor: string | null;
  onLoadMoreMessage?: (after: string) => void;
};

export function useChatWindowScroll({
  conversationId,
  messages,
  isLoadingMessage,
  isLoadingMore,
  prevCursor,
  onLoadMoreMessage,
}: UseChatWindowScrollParams) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const isPinnedRef = useRef(true);
  const scrollingToBottomRef = useRef(false);
  const [showScrollChip, setShowScrollChip] = useState(false);
  const [unseenCount, setUnseenCount] = useState(0);
  const lockRef = useRef(false);
  const lastRequestedCursorRef = useRef<string | null>(null);
  const historyPositionRef = useRef<{ height: number; top: number } | null>(null);
  const didInitialScrollRef = useRef(false);
  const hasUserScrolledRef = useRef(false);
  const previousIdsRef = useRef(new Set<string>());
  const lastFirstMessageIdRef = useRef<string | null>(null);
  const lastTailMessageIdRef = useRef<string | null>(null);
  const prevScrollHeightRef = useRef<number>(0);
  const lastScrollTopRef = useRef<number>(0);

  useLayoutEffect(() => {
    isPinnedRef.current = true;
    hasUserScrolledRef.current = false;
    scrollingToBottomRef.current = false;
    didInitialScrollRef.current = false;
    previousIdsRef.current = new Set();
    lastFirstMessageIdRef.current = null;
    lastTailMessageIdRef.current = null;
    prevScrollHeightRef.current = 0;
    lastScrollTopRef.current = 0;
    historyPositionRef.current = null;
    lockRef.current = false;
    lastRequestedCursorRef.current = null;
    setShowScrollChip(false);
    setUnseenCount(0);
  }, [conversationId]);

  const scrollToBottom = useCallback((smooth: boolean) => {
    const el = scrollRef.current;
    if (!el) return;
    const top = Math.max(0, el.scrollHeight - el.clientHeight);
    isPinnedRef.current = true;
    hasUserScrolledRef.current = false;
    setShowScrollChip(false);
    setUnseenCount(0);
    // Avoid redundant scroll calls when already at the bottom
    if (!smooth && Math.abs(el.scrollTop - top) <= 2) {
      scrollingToBottomRef.current = false;
      prevScrollHeightRef.current = el.scrollHeight;
      lastScrollTopRef.current = el.scrollTop;
      return;
    }
    scrollingToBottomRef.current = smooth && Math.abs(el.scrollTop - top) > 2;
    // Scroll only the chat viewport; scrollIntoView can also move the page/dialog.
    if (smooth) {
      el.scrollTo({ top, behavior: 'smooth' });
    } else {
      el.scrollTop = top;
    }
    prevScrollHeightRef.current = el.scrollHeight;
    lastScrollTopRef.current = top;
  }, []);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const previousIds = previousIdsRef.current;
    const prevFirstId = lastFirstMessageIdRef.current;
    const prevTailId = lastTailMessageIdRef.current;
    const prevScrollHeight = prevScrollHeightRef.current;

    previousIdsRef.current = new Set(messages.map((message) => message.id));
    const currentFirstId = messages[0]?.id ?? null;
    const currentTailId = messages[messages.length - 1]?.id ?? null;
    lastFirstMessageIdRef.current = currentFirstId;
    lastTailMessageIdRef.current = currentTailId;

    if (!messages.length) {
      didInitialScrollRef.current = false;
      prevScrollHeightRef.current = el.scrollHeight;
      return;
    }

    // Perform initial scroll once when messages first arrive for this conversation
    if (!didInitialScrollRef.current) {
      didInitialScrollRef.current = true;
      scrollToBottom(false);
      return;
    }

    // Explicit pagination ("load more" when scrolling to top)
    const historyPosition = historyPositionRef.current;
    if (historyPosition) {
      if (!isLoadingMore) {
        el.scrollTop = historyPosition.top + (el.scrollHeight - historyPosition.height);
        historyPositionRef.current = null;
      }
      prevScrollHeightRef.current = el.scrollHeight;
      return;
    }

    // If the tail (newest) message has not changed to a brand-new message, this update
    // came from a background history/media refresh payload. Do NOT scroll to bottom again.
    const hasNewTailMessage =
      currentTailId !== null &&
      currentTailId !== prevTailId &&
      !previousIds.has(currentTailId);

    if (!hasNewTailMessage) {
      // If older messages were prepended at the top by refreshHistory, preserve the current
      // viewport anchor so the screen does not jump back and forth.
      const prependedOlderHistory =
        currentFirstId !== null &&
        currentFirstId !== prevFirstId &&
        !previousIds.has(currentFirstId);

      if (prependedOlderHistory && prevScrollHeight > 0 && el.scrollHeight > prevScrollHeight) {
        if (!hasUserScrolledRef.current && isPinnedRef.current) {
          el.scrollTop = Math.max(0, el.scrollHeight - el.clientHeight);
        } else {
          el.scrollTop += el.scrollHeight - prevScrollHeight;
        }
      }
      prevScrollHeightRef.current = el.scrollHeight;
      lastScrollTopRef.current = el.scrollTop;
      return;
    }

    // Genuinely new message(s) arrived at the bottom of the conversation
    let newBottomCount = 0;
    for (let i = messages.length - 1; i >= 0; i--) {
      if (previousIds.has(messages[i].id)) break;
      newBottomCount++;
    }
    if (!newBottomCount) {
      prevScrollHeightRef.current = el.scrollHeight;
      return;
    }

    if (!hasUserScrolledRef.current && isPinnedRef.current) {
      scrollToBottom(false);
    } else {
      setUnseenCount((count) => count + newBottomCount);
      setShowScrollChip(true);
      prevScrollHeightRef.current = el.scrollHeight;
    }
  }, [messages, isLoadingMessage, isLoadingMore, scrollToBottom]);

  useEffect(() => {
    if (!isLoadingMore) lockRef.current = false;
    if (lastRequestedCursorRef.current !== prevCursor) lastRequestedCursorRef.current = null;
  }, [isLoadingMore, prevCursor]);

  useEffect(() => {
    const el = scrollRef.current;
    const content = contentRef.current;
    if (!el || !content || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => {
      // Do not re-scroll if the user has already scrolled up or during history pagination
      if (
        didInitialScrollRef.current &&
        !hasUserScrolledRef.current &&
        isPinnedRef.current &&
        !historyPositionRef.current
      ) {
        const top = Math.max(0, el.scrollHeight - el.clientHeight);
        if (Math.abs(el.scrollTop - top) > 2) {
          el.scrollTop = top;
        }
        prevScrollHeightRef.current = el.scrollHeight;
        lastScrollTopRef.current = el.scrollTop;
      }
    });
    observer.observe(content);
    observer.observe(el);
    return () => {
      observer.disconnect();
    };
  }, [conversationId]);

  const onDivScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    // Intermediate events from smooth scrolling must not cancel bottom-following.
    if (scrollingToBottomRef.current && distance > 2) return;
    scrollingToBottomRef.current = false;

    const scrolledUp = el.scrollTop < lastScrollTopRef.current - 2;
    lastScrollTopRef.current = el.scrollTop;

    if (scrolledUp) {
      hasUserScrolledRef.current = true;
      isPinnedRef.current = false;
    } else if (distance <= 20) {
      hasUserScrolledRef.current = false;
      isPinnedRef.current = true;
    } else if (distance >= BOTTOM_THRESHOLD) {
      hasUserScrolledRef.current = true;
      isPinnedRef.current = false;
    }

    const pinned = isPinnedRef.current;
    setShowScrollChip(!pinned);
    if (pinned) setUnseenCount(0);

    if (
      didInitialScrollRef.current && !isLoadingMessage && !pinned
      && el.scrollTop <= TOP_THRESHOLD && !lockRef.current && !isLoadingMore
      && onLoadMoreMessage && prevCursor && lastRequestedCursorRef.current !== prevCursor
    ) {
      lockRef.current = true;
      lastRequestedCursorRef.current = prevCursor;
      historyPositionRef.current = { height: el.scrollHeight, top: el.scrollTop };
      onLoadMoreMessage(prevCursor);
    }
  }, [isLoadingMessage, isLoadingMore, onLoadMoreMessage, prevCursor]);

  const onUserScroll = useCallback(() => {
    scrollingToBottomRef.current = false;
  }, []);

  const setPinnedToBottom = useCallback(() => {
    isPinnedRef.current = true;
    hasUserScrolledRef.current = false;
  }, []);

  return {
    scrollRef,
    contentRef,
    onDivScroll,
    onUserScroll,
    showScrollChip,
    unseenCount,
    scrollToBottom,
    handleScrollChipClick: () => scrollToBottom(true),
    setPinnedToBottom,
  };
}
